<template>
  <span
    class="sv-badge"
    :class="`sv-badge--${alertKey}`"
    :title="hoverMode ? null : tooltip"
    :aria-label="tooltip"
    role="img"
    :data-test="`sv-badge-${alertKey}`"
    @mouseenter="onEnter"
  ><i class="fa" :class="icon" aria-hidden="true"></i></span>
</template>

<script>
/**
 * One alert icon on an appointment block.
 *
 * ── WHY THE TOOLTIP IS THE WHOLE SENTENCE ────────────────────────────────────────────────────
 * The icon is the glanceable part; the sentence is what makes it actionable. "Overdue for a
 * cleaning by 112 days, with none booked" tells somebody what to DO. A bare icon means they have
 * to remember eleven symbols, and they will not — they will click into the patient, which is the
 * exact cost this page exists to remove.
 *
 * `title` rather than a custom popover on purpose: it works on a block that is 22px tall, it works
 * under the broken-appointment overlay, it never gets clipped by the grid's `overflow: auto`, and
 * it is read out by screen readers. A designed tooltip would need all four solved again.
 *
 * ⚠️ Colour is NOT the signal. Every badge is the same muted ink, deliberately: the status dot
 * already owns colour on this block, and a row of competing colours makes a busy day unreadable.
 * The icon shape carries the meaning, which also survives the colour-blind case.
 *
 * ── HOVER MODE (the visit checklist, Phase 10e) ───────────────────────────────────────────────
 * With a mouse, resting on the block opens the checklist card — and the native `title` bubble would
 * open ON TOP of it, over the very sentence it repeats. So in hover mode the `title` is dropped and
 * the `aria-label` (which screen readers read, and which has the same sentence) stays. Hovering an
 * icon tells the layer which alert it is, so the card can highlight and scroll to that category.
 * On a touch device there is no hover card to collide with, so the `title` stays.
 */
import { ALERT_META } from '@/utils/scheduleView/settingsForm';
import { describeAlert } from '@/utils/scheduleView/alerts';

const ICON_BY_KEY = {};
const LABEL_BY_KEY = {};
ALERT_META.forEach((a) => {
  ICON_BY_KEY[a.key] = a.icon;
  LABEL_BY_KEY[a.key] = a.label;
});

export default {
  name: 'AlertBadge',

  props: {
    /**
     * ⛔ Named `alertKey`, not `key`: `key` is reserved by Vue and would never reach the component.
     * Also not `patNum`/`siteID`/`loading` — mixin.js computeds silently replace those.
     */
    alertKey: { type: String, required: true },
    /** The detail object from the /alerts payload. Always an object, possibly empty. */
    detail: { type: Object, default: () => ({}) },
    /** True while the checklist opens on hover (a mouse). Drops the native title; see the header. */
    hoverMode: { type: Boolean, default: false },
  },

  methods: {
    onEnter() {
      // Only meaningful while a hover card can exist; on touch a synthesised mouseenter is noise.
      if (this.hoverMode) this.$emit('hover-alert', this.alertKey);
    },
  },

  computed: {
    icon() {
      return ICON_BY_KEY[this.alertKey] || 'fa-exclamation-circle';
    },

    tooltip() {
      const label = LABEL_BY_KEY[this.alertKey] || this.alertKey;
      const sentence = describeAlert(this.alertKey, this.detail);
      return sentence ? `${label} — ${sentence}` : label;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
  font-size: 9.5px;
  line-height: 1;
  // One muted ink for every alert — see the note in the script block. The status dot owns colour.
  color: $sv-ink-2;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 3px;
}
</style>
